/**
 * Granbay v6 mobile search sheet.
 *
 * Fullscreen-from-bottom sheet triggered from the bottom-nav Search
 * tab. Mirrors Polymarket mobile search: search input + BROWSE
 * chips + TOPICS button grid. Mono Lucide icons throughout (no
 * colorful illustrated icons per the platform icon rule).
 *
 * Closes on backdrop tap, ✕ button, or pressing Escape.
 */
"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import {
  Search,
  Sparkles,
  TrendingUp,
  Flame,
  Droplet,
  TimerReset,
  Swords,
  Bitcoin,
  Landmark,
  Globe,
  Trophy,
  Clapperboard,
  Cpu,
  Bot,
  X,
  type LucideIcon,
} from "lucide-react";
import { api, type MarketListItem } from "@/lib/api";

interface BrowseChip {
  label: string;
  icon: LucideIcon;
  href: string;
}

const BROWSE: BrowseChip[] = [
  { label: "New", icon: Sparkles, href: "/markets?sort=opens_desc" },
  { label: "Trending", icon: TrendingUp, href: "/markets?sort=trending" },
  { label: "Popular", icon: Flame, href: "/markets?sort=volume" },
  { label: "Liquid", icon: Droplet, href: "/markets?sort=liquidity" },
  { label: "Ending Soon", icon: TimerReset, href: "/markets?sort=closes_asc" },
  { label: "Competitive", icon: Swords, href: "/markets?sort=competitive" },
];

interface TopicTile {
  label: string;
  icon: LucideIcon;
  href: string;
}

const TOPICS: TopicTile[] = [
  { label: "Live Crypto", icon: TrendingUp, href: "/markets?category=crypto&live=1" },
  { label: "Politics", icon: Landmark, href: "/markets?category=politics" },
  { label: "Middle East", icon: Globe, href: "/markets?topic=middle-east" },
  { label: "Crypto", icon: Bitcoin, href: "/markets?category=crypto" },
  { label: "Sports", icon: Trophy, href: "/markets?category=sports" },
  { label: "Pop Culture", icon: Clapperboard, href: "/markets?category=culture" },
  { label: "Tech", icon: Cpu, href: "/markets?category=tech" },
  { label: "AI", icon: Bot, href: "/markets?topic=ai" },
];

export function V6MobileSearchSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MarketListItem[]>([]);
  const [searching, setSearching] = useState(false);

  // Close on Escape.
  // 2026-05-24: body-overflow lock removed entirely — it was the
  // proximate cause of the macbook-touchpad scroll regression founder
  // caught ("still not working, why did you break something simple as
  // scrow using the macbook laptop touchpad on mobile"). The sheet is
  // already `position: fixed; inset: 0;` so it visually covers the
  // viewport; the page beneath can scroll a bit but the user is
  // looking at the sheet, not the page. Worth the trade vs locking
  // body scroll site-wide.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Clear state when the sheet closes so reopening doesn't show stale
  // results from the previous session.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  // Debounced live search — fires ~220ms after the user stops typing.
  // Founder caught 2026-05-24: the input was unhooked, no filtering.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      try {
        const res = await api.listMarkets({ q, limit: 10 });
        if (!cancelled) setResults(res.markets);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query]);

  // Filter the static chips/tiles by query so even before the API
  // responds, the user sees an immediate "match" affordance.
  const filteredBrowse = useMemo(
    () => filterByQuery(BROWSE, query),
    [query],
  );
  const filteredTopics = useMemo(
    () => filterByQuery(TOPICS, query),
    [query],
  );

  if (!open) return null;

  const showStatic = query.trim().length < 2;

  return (
    <div
      className="v6-search-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        background: "var(--color-bg)",
        color: "var(--color-text)",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        overflowY: "auto",
      }}
    >
      {/* Grab-handle + close */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 10,
          paddingBottom: 6,
          position: "relative",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: "var(--color-border2)",
          }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close search"
          style={{
            position: "absolute",
            right: 12,
            top: 8,
            background: "transparent",
            border: "none",
            padding: 6,
            color: "var(--color-text2)",
            cursor: "pointer",
          }}
        >
          <X size={18} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </div>

      {/* Search input */}
      <div style={{ padding: "8px 14px 6px" }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
          }}
        >
          <Search
            size={16}
            strokeWidth={1.75}
            aria-hidden="true"
            style={{ color: "var(--color-text2)" }}
          />
          <input
            type="search"
            placeholder="Search granbay…"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search markets, events, creators"
            autoComplete="off"
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 15,
              fontFamily: "var(--font-sans)",
              color: "var(--color-text)",
            }}
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              style={{
                background: "transparent",
                border: "none",
                padding: 4,
                color: "var(--color-text3)",
                cursor: "pointer",
              }}
            >
              <X size={14} strokeWidth={1.75} aria-hidden="true" />
            </button>
          )}
        </label>
      </div>

      {showStatic ? (
        <>
          {/* BROWSE */}
          <SectionHeader label="Browse" />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 10,
              padding: "0 14px",
            }}
          >
            {BROWSE.map((c) => (
              <BrowseTile key={c.label} chip={c} onSelect={onClose} />
            ))}
          </div>

          {/* CATEGORIES — square tiles (Polymarket / App Store pattern). */}
          <SectionHeader label="Categories" />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 10,
              padding: "0 14px 24px",
            }}
          >
            {TOPICS.map((t) => (
              <CategorySquare key={t.label} tile={t} onSelect={onClose} />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Live API market results (debounced ~220ms after typing). */}
          <SectionHeader
            label={searching ? "Searching…" : `Results · ${results.length}`}
          />
          {results.length > 0 ? (
            <ul
              style={{
                listStyle: "none",
                padding: "0 14px 12px",
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {results.map((m) => (
                <li key={m.id}>
                  <Link
                    href={`/markets/${m.slug}`}
                    onClick={onClose}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid var(--color-border)",
                      background: "var(--color-surface)",
                      color: "var(--color-text)",
                      textDecoration: "none",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background:
                          m.yesPriceCents !== null
                            ? "var(--color-accent)"
                            : "var(--color-text3)",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.title?.en ?? m.slug}
                    </span>
                    {m.yesPriceCents !== null && (
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--color-text2)",
                          flexShrink: 0,
                        }}
                      >
                        {m.yesPriceCents}¢
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          ) : !searching ? (
            <div
              style={{
                padding: "8px 14px 16px",
                fontSize: 13,
                color: "var(--color-text3)",
              }}
            >
              No markets match &ldquo;{query}&rdquo;.
            </div>
          ) : null}

          {/* Quick-jump chips matching the typed term (e.g. typing
              "cry" surfaces the Crypto category tile alongside the
              live API results). */}
          {(filteredBrowse.length > 0 || filteredTopics.length > 0) && (
            <>
              <SectionHeader label="Jump to" />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 10,
                  padding: "0 14px 24px",
                }}
              >
                {filteredBrowse.map((c) => (
                  <BrowseTile key={c.label} chip={c} onSelect={onClose} />
                ))}
                {filteredTopics.map((t) => (
                  <CategorySquare key={t.label} tile={t} onSelect={onClose} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function filterByQuery<T extends { label: string }>(items: T[], q: string): T[] {
  const needle = q.trim().toLowerCase();
  if (needle.length < 2) return [];
  return items.filter((it) => it.label.toLowerCase().includes(needle));
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: "20px 14px 8px",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "var(--color-text3)",
      }}
    >
      {label}
    </div>
  );
}

function BrowseTile({
  chip,
  onSelect,
}: {
  chip: BrowseChip;
  onSelect: () => void;
}) {
  const Icon = chip.icon;
  return (
    <Link
      href={chip.href}
      onClick={onSelect}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid var(--color-border)",
        background: "var(--color-surface)",
        color: "var(--color-text)",
        textDecoration: "none",
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
      <span>{chip.label}</span>
    </Link>
  );
}

function CategorySquare({
  tile,
  onSelect,
}: {
  tile: TopicTile;
  onSelect: () => void;
}) {
  const Icon = tile.icon;
  return (
    <Link
      href={tile.href}
      onClick={onSelect}
      style={{
        aspectRatio: "1 / 1",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: 8,
        borderRadius: 14,
        border: "1px solid var(--color-border)",
        background: "var(--color-surface)",
        color: "var(--color-text)",
        textDecoration: "none",
        fontSize: 12,
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--color-accent)",
        }}
      >
        <Icon size={22} strokeWidth={1.75} aria-hidden="true" />
      </span>
      <span style={{ lineHeight: 1.2 }}>{tile.label}</span>
    </Link>
  );
}
